import { Centrifuge } from 'centrifuge';
import { jwtDecode } from 'jwt-decode';
import dayjs from 'dayjs';

export function useWebsockets() {
  const { apiUrl } = useRuntimeConfig().public;

  const cartStore = useCartStore();

  async function initWebSockets() {
    try {
      let tokens = await fetchJwtToken();
      saveTokens(tokens.access_token, tokens.refresh_token);

      const { websocketsUri } = useRuntimeConfig().public;

      const centrifuge = new Centrifuge(
        `wss://${import.meta.env.PROD ? websocketsUri : 'ws.okdev.ru'}/connection/websocket`,
        { getToken: getConnectionToken },
      );

      // Обработка событий соединения и само соединение
      centrifuge
        // .on('connecting', (ctx) => console.log(`connecting: ${ctx.code}, ${ctx.reason}`, dayjs().format()))
        // .on('connected', (ctx) => console.log(`connected over ${ctx.transport}`, dayjs().format()))
        // .on('disconnected', (ctx) => console.log(`disconnected: ${ctx.code}, ${ctx.reason}`, dayjs().format()))
        .connect();

      // Подписка на публичный канал
      const publicSub = centrifuge.newSubscription('public:messages');
      publicSub
        // .on('publication', (ctx) => console.log(ctx.data))
        .subscribe();

      // Декодируем токен для получения userId
      const decodedToken = jwtDecode(tokens.access_token);
      const userId = decodedToken.sub;
      // console.log(userId);

      // Подписка на канал с корзиной
      const privateCartSub = centrifuge.newSubscription(`cart:${userId}`, {
        getToken: getChannelSubscriptionToken,
      });
      privateCartSub
        .on('publication', (ctx) => {
          if (ctx.data?.message) cartStore.setCartStoreState(JSON.parse(ctx.data.message));
        })
        .subscribe();
    } catch (error) {
      console.error('Error setting up Centrifuge:', error);
    }
  }

  async function getConnectionToken() {
    const token = localStorage.getItem('access_token');

    if (token) {
      const decodedToken = jwtDecode(token);
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken?.exp && decodedToken.exp < currentTime) {
        // console.log('Token expired, refreshing...', dayjs().format());
        try {
          const newTokens = await refreshJwtToken();
          saveTokens(newTokens.access_token, newTokens.refresh_token);

          return newTokens.access_token;
        } catch (error) {
          console.error('Error refreshing JWT token:', error);
          // В случае ошибки, возможно нужно переинициализировать Centrifuge или предпринять другие действия
          return null;
        }
      } else {
        return token;
      }
    } else {
      return await fetchJwtToken();
    }
  }

  async function getChannelSubscriptionToken(ctx: any) {
    try {
      const sub = getSubFromToken();

      const response = await fetch(`${apiUrl}1/jwt/subscription-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getConnectionToken()}`,
        },
        body: JSON.stringify({ channel: ctx.channel, sub }),
      });
      if (!response.ok) throw new Error('Subscription token refresh failed');

      const data = await response.json();
      if (data.token) return data.token;

      throw new Error('No subscription token in response');
    } catch (e) {
      throw e;
    }
  }

  function getSubFromToken() {
    const token = localStorage.getItem('access_token');
    if (!token) return null;

    const payload = JSON.parse(atob(token.split('.')[1]));

    return payload.sub;
  }

  async function fetchJwtToken() {
    try {
      const response = await fetch(`${apiUrl}1/jwt/token`);

      return await response.json();
    } catch (error) {
      throw new Error('Failed to fetch JWT token');
    }
  }

  async function refreshJwtToken() {
    try {
      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken) {
        const formData = new FormData();
        formData.append('refresh_token', refreshToken);

        const response = await fetch(`${apiUrl}1/jwt/refresh`, {
          method: 'POST',
          body: formData,
          // Нет необходимости добавлять Content-Type, так как FormData автоматически устанавливает его
        });

        if (response.status === 401) {
          // Refresh токен просрочен, запрашиваем новые токены
          // console.log('Refresh token expired, fetching new tokens...', dayjs().format());
          return await fetchJwtToken();
        } else {
          return await response.json();
        }
      } else {
        return await fetchJwtToken();
      }
    } catch (error) {
      throw new Error('Failed to refresh JWT token');
    }
  }

  function saveTokens(accessToken: string, refreshToken?: string) {
    localStorage.setItem('access_token', accessToken);

    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
  }

  return {
    initWebSockets,
  };
}
